/*
 * @Author: yidan.chen
 * @Date: 2025-02-07 14:48:36
 * @Last Modified by: yidan.chen
 * @Last Modified time: 2025-04-08 16:57:14
 */
import React, { useRef } from 'react'
import { TextField, Grid2 as Grid, MenuItem } from '@mui/material'
import MultiSelect, {
  MultiSelectProps
} from '@/components/FormItem/MultiSelect'
import CustomDateRangePicker from './CustomDateRangePicker'
import CustomDateTimePicker from './CustomDateTimePicker'
import DateRangePicker from './DateRangePicker'
import AutoComplete from './AutoComplete'

const formItemSize = { xs: 12, sm: 6, md: 4, lg: 2.2, xl: 1.5 }

export enum TFormType {
  'TextFiled',
  'AutoComplete',
  'MultiSelect',
  'DateRangePicker',
  'MultiOptionTimeRange',
  'Select',
  'DateTimePicker'
}

export interface IFormItemConf {
  label: string
  name: string
  formType: TFormType
  optionListName?: string
  defaultValue?: any
  extraProps?: Record<string, any>
  size?: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> | number
  key?: string
  allCheckedValue?: string
  timeRangeName?: {
    start: string
    end: string
  }
  selectLabel?: string
  placeholder?: string
  required?: boolean
  presetRanges?: {
    label: string
    days: number
  }[]
  dateType?: 'date' | 'datetime'
}

interface IFormItemProps {
  item: IFormItemConf
  optionList?: Record<string, Array<Record<string, any>>>
  onChangeValue?: ({ value, name }: { value: any; name: string }) => void
}

const FormItem = React.memo((formItemProps: IFormItemProps) => {
  const { item, optionList = {}, onChangeValue } = formItemProps
  const inputRef = useRef<HTMLInputElement>(null)

  // render form item based on different formType
  switch (item.formType) {
    case TFormType.TextFiled:
      return (
        <Grid size={item.size ?? formItemSize} key={item.name}>
          <TextField
            key={item.name}
            name={item.name}
            label={item.label}
            defaultValue={item.defaultValue}
            variant='outlined'
            size='small'
            inputRef={inputRef}
            onChange={e => {
              const transformedValue = item.extraProps?.transformValue?.(
                e.target.value
              )
              onChangeValue?.({
                value: transformedValue ?? e.target.value,
                name: item.name
              })
            }}
            onBlur={e => {
              if (item.extraProps?.transformValue && inputRef.current) {
                const transformedValue = item.extraProps?.transformValue?.(
                  e.target.value
                )
                onChangeValue?.({
                  value: transformedValue ?? e.target.value,
                  name: item.name
                })
                inputRef.current.value = transformedValue
              }
            }}
            sx={{
              width: '100%',
              height: '36px',
              '& .MuiInputBase-root': {
                height: '36px'
              },
              '& input[type="date"]': {
                height: '20px',
                padding: '8px 14px'
              },
              ...(item.extraProps?.sx ?? {})
            }}
            placeholder={item.placeholder ?? ''}
            {...(item.required ? { required: true } : {})}
            {...(item.extraProps
              ? {
                  ...Object.fromEntries(
                    Object.entries(item.extraProps).filter(
                      ([key]) => key !== 'sx' && key !== 'transformValue'
                    )
                  )
                }
              : {})}
          />
        </Grid>
      )
    case TFormType.AutoComplete:
      return (
        <Grid size={item.size ?? formItemSize} key={item.name}>
          <AutoComplete
            name={item.name}
            label={item.label}
            optionList={
              item.optionListName ? optionList[item.optionListName] || [] : []
            }
            placeholder={item.placeholder}
            required={item.required}
            onChange={value => onChangeValue?.({ value, name: item.name })}
            defaultValue={item.defaultValue}
            extraProps={item.extraProps}
          />
        </Grid>
      )
    case TFormType.Select:
      return (
        <Grid size={item.size ?? formItemSize} key={item.name}>
          <TextField
            select
            key={item.name}
            name={item.name}
            label={item.label}
            onChange={e => {
              onChangeValue?.({ value: e.target.value, name: item.name })
            }}
            defaultValue={item.defaultValue || ''}
            variant='outlined'
            size='small'
            sx={{
              width: '100%',
              height: '36px',
              ...(item.extraProps?.sx ?? {})
            }}
            {...(item.extraProps
              ? {
                  ...Object.fromEntries(
                    Object.entries(item.extraProps).filter(
                      ([key]) => key !== 'sx' && key !== 'options'
                    )
                  )
                }
              : {})}>
            {item.extraProps?.options?.map((option: any) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}

            {item.optionListName &&
              optionList[item.optionListName]?.map((option: any) => (
                <MenuItem
                  key={option.value || option.id}
                  value={option.value || option.id}>
                  {option.label}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
      )

    case TFormType.MultiSelect:
      return (
        <Grid size={item.size ?? formItemSize} key={item.name}>
          <MultiSelect
            name={item.name}
            label={item.label}
            onChange={e => {
              onChangeValue?.({ value: e, name: item.name })
            }}
            optionList={
              (item.optionListName
                ? optionList[item.optionListName] || []
                : []) as MultiSelectProps['optionList']
            }
            allCheckedValue={item.allCheckedValue}
            extra={item.extraProps}
            defaultValue={item.defaultValue}
          />
        </Grid>
      )

    case TFormType.DateRangePicker:
      return (
        <Grid
          sx={{
            flex: 1,
            minWidth: '300px',
            maxWidth: '400px'
          }}
          key={item.name}>
          <DateRangePicker
            name={item.name}
            label={item.label}
            timeRangeName={item.timeRangeName}
            selectLabel={item.selectLabel}
            optionListName={item.optionListName}
            extraProps={item.extraProps}
            optionList={optionList}
            presetRanges={item.presetRanges}
          />
        </Grid>
      )

    case TFormType.DateTimePicker:
      return (
        <Grid size={item.size ?? formItemSize} key={item.name}>
          <CustomDateTimePicker
            name={item.name}
            label={item.label}
            dateType={item.dateType}
            required={item.required}
            onChange={e =>
              onChangeValue?.({ value: e.target.value, name: item.name })
            }
            extraProps={item.extraProps}
          />
        </Grid>
      )

    case TFormType.MultiOptionTimeRange:
      return (
        <Grid
          sx={{
            flex: 1,
            minWidth: '614px',
            maxWidth: '740px'
          }}
          key={item.name}>
          <CustomDateRangePicker
            name={item.name}
            label={item.label}
            timeRangeName={item.timeRangeName}
            selectLabel={item.selectLabel}
            optionListName={item.optionListName}
            extraProps={item.extraProps}
            optionList={optionList}
            presetRanges={item.presetRanges}
          />
        </Grid>
      )

    default:
      return null
  }
})

export default FormItem
